import type { EffectiveConfiguration } from '@/features/societies/types/society-configuration.types';

export type StripeBannerLevel = 'info' | 'warning' | 'danger';

/**
 * Sous-ensemble de la configuration effective réellement lu par
 * `deriveStripeBannerState`. Permet de dériver l'état du bandeau à partir d'une
 * source allégée (ex. liste multi-société) sans charger la config complète.
 * Une `EffectiveConfiguration` complète reste assignable à ce type.
 */
export type StripeBannerConfigInput = Pick<
  EffectiveConfiguration,
  | 'stripeAccountId'
  | 'stripeChargesEnabled'
  | 'stripePayoutsEnabled'
  | 'stripeDetailsSubmitted'
  | 'stripeRequirementsDue'
  | 'stripeFutureRequirementsDue'
  | 'stripeDisabledReason'
  | 'stripeCurrentDeadline'
  | 'stripeLastSyncedAt'
>;

export interface StripeBannerState {
  level: StripeBannerLevel;
  message: string;
  /**
   * Code Stripe brut (disabled_reason). À afficher uniquement pour les internals
   * (debug/support). Les externals ne le voient pas.
   */
  codeHint?: string;
  /**
   * Si présent → bouton qui ouvre la modale embedded.
   * Si absent → bandeau informatif sans action.
   */
  cta?: {
    label: string;
  };
}

const REJECTED_MESSAGES: Record<string, string> = {
  'rejected.fraud':
    'Votre compte Stripe a été rejeté suite à une suspicion de fraude.',
  'rejected.listed':
    'Votre compte Stripe a été rejeté suite à un contrôle de conformité.',
  'rejected.terms_of_service':
    'Votre compte Stripe a été rejeté pour non-respect des conditions générales de Stripe.',
  'rejected.incomplete_verification':
    'Votre compte Stripe a été rejeté : la vérification n\'a pas pu être complétée.',
  'rejected.platform_fraud':
    'Votre compte a été rejeté par notre plateforme suite à une suspicion de fraude.',
  'rejected.platform_terms_of_service':
    'Votre compte a été rejeté par notre plateforme pour non-respect des conditions générales.',
  'rejected.platform_other':
    'Votre compte a été rejeté par notre plateforme.',
  'rejected.other':
    'Votre compte Stripe a été rejeté.',
};

const INFORMATIVE_MESSAGES: Record<string, string> = {
  'listed':
    'Votre compte fait l\'objet d\'une revue de conformité par Stripe. Aucune action n\'est requise de votre part pour le moment.',
  'under_review':
    'Votre compte est en cours de revue manuelle par Stripe. Vous serez notifié si une action est requise.',
  'requirements.pending_verification':
    'Stripe est en train de vérifier les informations que vous avez fournies. Cela peut prendre quelques jours.',
  'platform_paused':
    'Votre compte est temporairement en pause.',
  'other':
    'Votre compte est temporairement suspendu pour vérification.',
};

const INFORMATIVE_REASONS = new Set(Object.keys(INFORMATIVE_MESSAGES));

/**
 * Dérive l'état de la bannière Stripe à partir de la configuration effective V2
 * et du `monthlyPayment` legacy (utilisé comme fallback pour distinguer un compte
 * jamais synchronisé d'un compte déjà finalisé avant l'ajout des colonnes
 * stripe_*).
 *
 * Couvre 25 cas (22 branches logiques + 3 fallbacks). Ordre de priorité strict :
 *   1. Pas d'account → null
 *   2. Rejected.* (terminal, pas de CTA)
 *   3. Platform_disabled (terminal plateforme, pas de CTA)
 *   4. Informatif (listed, under_review, pending_verification, etc. — pas de CTA)
 *   5. Actionnable past_due (CTA "Régulariser")
 *   6. Actionnable action_required.requested_capabilities (CTA "Compléter")
 *   7. Actionnable requirements_due > 0 sans disabledReason (CTA "Compléter")
 *   8. Actionnable future_requirements_due > 0 (CTA "Compléter" + deadline)
 *   9. Onboarding incomplet (CTA "Finaliser")
 *  10-11. Fallback charges/payouts off (informatif)
 *  12. Sain → null
 *
 * Fonction pure et stable.
 */
export function deriveStripeBannerState(
  config: StripeBannerConfigInput | undefined,
  legacyMonthlyPayment: number | undefined,
): StripeBannerState | null {
  if (!config?.stripeAccountId) {
    return null;
  }

  const {
    stripeChargesEnabled,
    stripePayoutsEnabled,
    stripeDetailsSubmitted,
    stripeRequirementsDue,
    stripeFutureRequirementsDue,
    stripeDisabledReason,
    stripeCurrentDeadline,
    stripeLastSyncedAt,
  } = config;

  const reason = stripeDisabledReason ?? null;

  // 1. Compte rejeté (terminal) — aucune action possible
  if (reason && reason.startsWith('rejected.')) {
    return {
      level: 'danger',
      message:
        REJECTED_MESSAGES[reason] ?? 'Votre compte Stripe a été rejeté.',
      codeHint: reason,
    };
  }

  // 2. Plateforme désactivée — aucune action côté vendeur
  if (reason === 'platform_disabled') {
    return {
      level: 'danger',
      message: 'Votre compte a été désactivé par notre plateforme.',
      codeHint: reason,
    };
  }

  // 3. Informatif (revue Stripe en cours) — aucune action
  if (reason && INFORMATIVE_REASONS.has(reason)) {
    return {
      level: 'warning',
      message: INFORMATIVE_MESSAGES[reason],
      codeHint: reason,
    };
  }

  // 4. Actionnable — past_due (informations en retard)
  if (reason === 'requirements.past_due') {
    return {
      level: 'danger',
      message:
        'Action urgente : vos informations Stripe sont en retard. Régularisez maintenant pour reprendre les paiements.',
      codeHint: reason,
      cta: { label: 'Régulariser mon compte' },
    };
  }

  // 5. Actionnable — capability requested attend des infos
  if (reason === 'action_required.requested_capabilities') {
    return {
      level: 'warning',
      message:
        'Action requise pour activer toutes les fonctionnalités de votre compte Stripe.',
      codeHint: reason,
      cta: { label: 'Compléter mes informations' },
    };
  }

  // 6. Actionnable — requirements_due > 0 sans disabledReason
  if ((stripeRequirementsDue ?? 0) > 0) {
    return {
      level: 'danger',
      message:
        'Action requise : Stripe demande des informations supplémentaires sur votre compte.',
      cta: { label: 'Compléter mes informations' },
    };
  }

  // 7. Actionnable — Restricted Soon (future_requirements avec deadline)
  if ((stripeFutureRequirementsDue ?? 0) > 0) {
    const deadlineText = stripeCurrentDeadline
      ? ` avant le ${new Date(stripeCurrentDeadline).toLocaleDateString('fr-FR')}`
      : '';
    return {
      level: 'warning',
      message: `Stripe vous demande de nouvelles informations${deadlineText} pour éviter une suspension de votre compte.`,
      cta: { label: 'Compléter mes informations' },
    };
  }

  // 8. Onboarding incomplet (jamais synchronisé OU details_submitted=false)
  const neverSynced = stripeLastSyncedAt == null;
  const notSubmitted = stripeDetailsSubmitted === false;
  if ((neverSynced && legacyMonthlyPayment !== 2) || notSubmitted) {
    return {
      level: 'info',
      message:
        'L\'onboarding Stripe n\'est pas terminé. Finalisez la création de votre compte Stripe pour pouvoir mensualiser vos contrats.',
      cta: { label: 'Finaliser l\'onboarding Stripe' },
    };
  }

  // 9. Fallback — charges désactivés sans raison explicite
  if (stripeChargesEnabled === false && stripeDetailsSubmitted) {
    return {
      level: 'warning',
      message: 'Vos encaissements sont temporairement suspendus.',
    };
  }

  // 10. Fallback — payouts désactivés sans raison explicite
  if (stripePayoutsEnabled === false && stripeChargesEnabled !== false) {
    return {
      level: 'warning',
      message:
        'Vos virements sont temporairement suspendus, en attente d\'une revue Stripe.',
    };
  }

  // 11. Compte sain
  return null;
}
